import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useNotification } from "../context/NotificationContext";
import {
 Mail,
 Phone,
 MapPin,
 Send,
 Loader2,
 MessageSquare,
 CheckCircle,
} from "lucide-react";
import api from "../api/client";

export default function ContactUs() {
 const { add } = useNotification();
 const TELEGRAM_HANDLE = process.env.REACT_APP_TELEGRAM_HANDLE || "fewzfewzan";
 const [formData, setFormData] = useState({
 name: "",
 contact: "",
 subject: "",
 message: "",
 });
 const [loading, setLoading] = useState(false);
 const [success, setSuccess] = useState(false);

 const contactInfo = [
 {
 icon: Send,
 title: "Telegram",
 details: `@${TELEGRAM_HANDLE}`,
 link: `https://t.me/${TELEGRAM_HANDLE}`,
 description: "Reach us on Telegram — we usually reply within 24 hours",
 },
 {
 icon: MapPin,
 title: "Location",
 details: "Addis Ababa, Ethiopia",
 description: "Building the future of work in Ethiopia",
 },
 ];

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);

 try {
 await api.post("/contact", {
 name: formData.name,
 email: formData.contact, // Using 'email' key as backend expects it
 subject: formData.subject,
 message: formData.message,
 });

 setSuccess(true);
 add("Message sent successfully! We'll get back to you on Telegram.", "success");
 
 // Reset form
 setFormData({
 name: "",
 contact: "",
 subject: "",
 message: "",
 });

 setTimeout(() => setSuccess(false), 5000);
 } catch (error) {
 add("Failed to send message. Please try again.", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleChange = (e) => {
 setFormData({ ...formData, [e.target.name]: e.target.value });
 };

 return (
 <div className="min-h-screen ">
 {/* Hero Section */}
 <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-3xl mx-auto">
 <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
 Get in <span className="text-primary">Touch</span>
 </h1>
 <p className="text-base sm:text-lg md:text-xl text-muted-foreground">
 Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
 </p>
 </div>
 </div>
 </section>

 {/* Contact Info Cards */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
 {contactInfo.map((info, index) => (
 <Card key={index} className="text-center hover:shadow-lg hover:scale-105 transition-all duration-300 border-border">
 <CardHeader className="pb-3">
 <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 flex items-center justify-center">
 <info.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 </div>
 <CardTitle className="text-lg sm:text-xl">{info.title}</CardTitle>
 <CardDescription className="text-sm">{info.description}</CardDescription>
 </CardHeader>
 <CardContent>
 {info.link ? (
 <a
 href={info.link}
 target="_blank"
 rel="noopener noreferrer"
 className="text-primary hover:underline font-medium text-sm sm:text-base break-all"
 >
 {info.details}
 </a>
 ) : (
 <p className="font-medium text-sm sm:text-base">{info.details}</p>
 )}
 </CardContent>
 </Card>
 ))}
 </div>

 {/* Contact Form */}
 <div className="max-w-3xl mx-auto">
 <Card className="border-border shadow-lg">
 <CardHeader className="space-y-3">
 <div className="flex items-center gap-3 mb-2">
 <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 flex items-center justify-center">
 <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 </div>
 <CardTitle className="text-xl sm:text-2xl">Send us a Message</CardTitle>
 </div>
 <CardDescription className="text-sm sm:text-base">
 Fill out the form below and we'll get back to you within 24 hours
 </CardDescription>
 </CardHeader>
 <CardContent>
 {success && (
 <Alert className="mb-6 border-green-500/50 bg-green-500/100/10">
 <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-600 dark:text-green-400 text-sm">
 Your message has been sent successfully! We'll get back to you soon.
 </AlertDescription>
 </Alert>
 )}

 <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label htmlFor="name" className="text-sm sm:text-base">Your Name</Label>
 <Input
 id="name"
 name="name"
 value={formData.name}
 onChange={handleChange}
 placeholder="John Doe"
 className="h-10 sm:h-11"
 required
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="contact" className="text-sm sm:text-base">Telegram Handle / Contact</Label>
 <Input
 id="contact"
 name="contact"
 type="text"
 value={formData.contact}
 onChange={handleChange}
 placeholder="@username"
 className="h-10 sm:h-11"
 required
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label htmlFor="subject" className="text-sm sm:text-base">Subject</Label>
 <Input
 id="subject"
 name="subject"
 value={formData.subject}
 onChange={handleChange}
 placeholder="What is this about?"
 className="h-10 sm:h-11"
 required
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="message" className="text-sm sm:text-base">Message</Label>
 <Textarea
 id="message"
 name="message"
 value={formData.message}
 onChange={handleChange}
 placeholder="Tell us more about your inquiry..."
 rows={6}
 className="resize-none"
 required
 />
 </div>

 <Button
 type="submit"
 className="w-full h-11 sm:h-12"
 size="lg"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
 Sending...
 </>
 ) : (
 <>
 <Send className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
 Send Message
 </>
 )}
 </Button>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 {/* FAQ Section */}
 <section className="py-12 sm:py-16 md:py-20 ">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-8 sm:mb-12">
 <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Frequently Asked Questions</h2>
 <p className="text-muted-foreground text-sm sm:text-base">
 Quick answers to common questions
 </p>
 </div>

 <div className="space-y-4">
 {[
 {
 q: "How do I post a job?",
 a: "Register as a recruiter, complete your profile, and submit a job posting. Our admin team will review and approve it within 24 hours.",
 },
 {
 q: "Is it free for job seekers?",
 a: "Yes! Job seekers can browse jobs, apply, and manage applications completely free.",
 },
 {
 q: "How long does it take to get a response?",
 a: "We typically respond within 24 hours on business days. For urgent matters, reach out via Telegram.",
 },
 ].map((faq, i) => (
 <Card key={i} className="hover:shadow-md transition-shadow border-border">
 <CardHeader className="pb-3">
 <CardTitle className="text-base sm:text-lg">{faq.q}</CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-muted-foreground text-sm sm:text-base">{faq.a}</p>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 </section>
 </div>
 );
}
